import { useEffect } from "react";
import { landingFields } from "@pushback-and-chaos/client";
import type { AnalyticsRoute, VisitBeacon } from "@pushback-and-chaos/contracts";
import type { RouteName } from "../routes.js";
import { IS_PREVIEW, countVisit } from "./transport.js";

/**
 * FIRST-PARTY VISIT COUNTING, the browser's half (ADR 0022).
 *
 * One beacon per route the learner settles on: the route's NAME from the
 * table (never an id, never a query), and on the first view of this page
 * load only, the referring site's host (external only) and the three campaign
 * tags from the landing URL. Nothing is written to the device for counting:
 * the "first view" mark lives in this module's memory and goes with the page.
 *
 * NOTHING IS SENT when the browser sends Global Privacy Control or Do Not
 * Track, or when the privacy page's "don't count my visits." is on. That
 * switch is the one thing kept on the device: a plain flag in this browser's
 * localStorage that is read here and never sent anywhere — not as a header,
 * not in a body, not as a cookie. The server cannot tell an opted-out
 * browser from one that never came.
 */

/** The opt-out's one key. Its value is "1" or absent; nothing else is stored under it. */
export const DO_NOT_COUNT_KEY = "pnc.do-not-count";

/** How long a route must hold before it is counted: a redirect through it (landing → home) is not a view. */
export const SETTLE_MS = 800;

interface PrivacySignals {
  readonly globalPrivacyControl?: boolean;
  readonly doNotTrack?: string | null;
}

/** Global Privacy Control, or Do Not Track set to "1". */
export function browserAsksNotToCount(nav: PrivacySignals | undefined = globalThis.navigator): boolean {
  return nav?.globalPrivacyControl === true || nav?.doNotTrack === "1";
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

/** Whether the opt-out is on in this browser. Unreadable storage reads as off (the browser signals still hold). */
export function doNotCount(store: Storage | null = storage()): boolean {
  try {
    return store?.getItem(DO_NOT_COUNT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Sets or clears the opt-out; false when this browser would not keep it. */
export function setDoNotCount(on: boolean, store: Storage | null = storage()): boolean {
  if (store === null) return false;
  try {
    if (on) store.setItem(DO_NOT_COUNT_KEY, "1");
    else store.removeItem(DO_NOT_COUNT_KEY);
    return doNotCount(store) === on;
  } catch {
    return false;
  }
}

/**
 * Routes never counted: the two pages an email leads to (CME stage 3b,
 * 2026-10-11). A token-addressed page is one person's act on a consent, not a
 * visit: counting it would put a mail-driven event beside the site's visits,
 * and the outcomes totals already say how many answered. The account emails'
 * two pages (2026-10-22, `verify` and `reset`) carry a one-time token too,
 * and are left out for the same reason, and so is the region gate's page
 * (2026-10-28, `not-offered`), which is a refusal, not a visit, and whose
 * beacon the gate would refuse anyway. Every other route in the table is
 * counted by its own name: the contract's list is the same table less these
 * five, so a route added here and not there fails to compile, and the test
 * holds the other direction.
 */
const UNCOUNTED_ROUTES = new Set<RouteName>(["unsubscribe", "check-in", "verify", "reset", "not-offered"]);
type CountedRoute = Exclude<RouteName, "unsubscribe" | "check-in" | "verify" | "reset" | "not-offered">;
const isCounted = (route: RouteName): route is CountedRoute => !UNCOUNTED_ROUTES.has(route);

/** The name counted for a route, or null for one never counted (the token pages). */
export function countedRoute(route: RouteName): AnalyticsRoute | null {
  return isCounted(route) ? route : null;
}

let landingCounted = false;

/** For tests: forget that this page load's landing view was counted. */
export function resetLandingForTests(): void {
  landingCounted = false;
}

/** The beacon for a settled route, or null when nothing may be sent. */
export function beaconFor(route: RouteName, page: { readonly href: string; readonly referrer: string }, refused: boolean): VisitBeacon | null {
  const name = countedRoute(route);
  if (name === null || refused) return null;
  if (landingCounted) return { route: name };
  landingCounted = true;
  return { route: name, ...landingFields(page.href, page.referrer) };
}

/** Counts the route once it has held for SETTLE_MS, unless the browser or the opt-out says not to. */
export function useVisitCounting(route: RouteName): void {
  useEffect(() => {
    if (IS_PREVIEW) return undefined;
    const timer = globalThis.setTimeout(() => {
      const refused = browserAsksNotToCount() || doNotCount();
      const beacon = beaconFor(route, { href: globalThis.location.href, referrer: globalThis.document.referrer }, refused);
      if (beacon !== null) countVisit(beacon);
    }, SETTLE_MS);
    return () => globalThis.clearTimeout(timer);
  }, [route]);
}
